import { Dimensions, Platform } from 'react-native';

import { theme } from '@squire/ui';

export type ApiPlatform = 'iOS' | 'Android' | 'Web';

/**
 * The platform an appointment is recorded under. Widens `ApiPlatform` with
 * `Google`, which squire-api treats as an origin rather than a device (see
 * `appointment_platform_types.js`) and uses to attribute the appointment to
 * Google. Resolved by `getBookingPlatform`, not by device detection.
 */
export type BookingPlatform = ApiPlatform | 'Google';

/**
 * Utility function to get the platform name. This allows to be mocked in jest and is easier to read in code
 * @returns The platform name
 */
export function getPlatform() {
  return Platform.OS;
}

/**
 * Maps Platform.OS to the API-whitelisted platform strings used for appointment tracking.
 */
export function getApiPlatform(): ApiPlatform {
  return Platform.select({
    ios: 'iOS' as const,
    android: 'Android' as const,
    default: 'Web' as const,
  });
}

/**
 * Utility function to check if the platform is web
 * @returns True if the platform is web, false otherwise
 */
export function isWeb() {
  return getPlatform() === 'web';
}

/**
 * Utility function to check if the platform is iOS
 * @returns True if the platform is iOS, false otherwise
 */
export function isIOS() {
  return getPlatform().toLowerCase() === 'ios';
}

/**
 * Utility function to check if the platform is Android
 * @returns True if the platform is Android, false otherwise
 */
export function isAndroid() {
  return getPlatform().toLowerCase() === 'android';
}

/**
 * Utility function to check if the platform is native (iOS or Android)
 * @returns True if the platform is iOS or Android, false otherwise
 */
export function isNative() {
  return isIOS() || isAndroid();
}

/**
 * Utility function to check if the current viewport is mobile-sized
 * (narrower than the tablet breakpoint). Evaluated at call time, so it reflects
 * the current window width (e.g. responsive web, rotation).
 * @returns True if the window width is below the tablet breakpoint
 */
export function isMobile() {
  return Dimensions.get('window').width < theme.breakpoints.tablet;
}
